import {
  Table,
  Form,
  Tooltip,
  InputNumber,
  Input,
  Button,
  Modal,
  Tag,
  Progress,
} from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useState, useMemo, useEffect } from "react";
import { Spinner } from "reactstrap";
import {
  TrophyOutlined,
  EditOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CrownOutlined,
  TeamOutlined,
  AppstoreOutlined,
  StarOutlined,
} from "@ant-design/icons";
import EvaluationScoreDrawer from "./EvaluationScoreDrawer";
import tenderApis from "../../../api/tender/tenderApis";
import Swal from "sweetalert2";
import {
  colors as avatarColors,
  getSupplierInitials,
} from "./components/comparisonUtils";

const rankColors: Record<number, { bg: string; color: string; label: string }> =
  {
    1: { bg: "#fef3c7", color: "#a16207", label: "1st" },
    2: { bg: "#f3f4f6", color: "#6b7280", label: "2nd" },
    3: { bg: "#ffedd5", color: "#c2410c", label: "3rd" },
  };

const getScoreColor = (score: number, maxScore: number) => {
  const pct = score / maxScore;
  if (pct >= 0.8) return "#52c41a";
  if (pct >= 0.6) return "#faad14";
  return "#ff4d4f";
};

const ScoreCardView = ({ tenderData, activeTab, tenderID }: any) => {
  const [supplierEvaluations, setSupplierEvaluations] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchEvaluationResults = async () => {
      try {
        setLoading(true);
        const tenderId = tenderData?.tenderId || tenderID;
        const response = await tenderApis.getTenderEvaluationResults(
          Number(tenderId)
        );
        if (response?.status || response?.data) {
          const evalData =
            response?.data?.response || response?.data || {};
          const suppliers = (evalData?.supplierEvaluations || []).map(
            (s: any) => ({
              ...s,
              supplierID: s.supplierId || s.supplierID || s.bidId,
            })
          );
          setSupplierEvaluations(suppliers);
        }
      } catch (error) {
        console.log("Error fetching evaluation results:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvaluationResults();
  }, [tenderData?.tenderId, tenderID]);
  const [openEvaluationScoreDrawer, setOpenEvaluationScoreDrawer] =
    React.useState(false);
  const [scoreDetails, setScoreDetails] = useState<any>(null);
  const [currentSection, setCurrentSection] = useState<string>("");
  const [currentSupplier, setCurrentSupplier] = useState<string>("");
  const [currentSectionScore, setCurrentSectionScore] = useState<number>(0);
  const [currentMaxScore, setCurrentMaxScore] = useState<number>(100);
  const [currentQuestions, setCurrentQuestions] = useState<any[]>([]);

  // Editable overall score state
  const [adjustedScores, setAdjustedScores] = useState<
    Record<string, { score: number; reason: string; originalScore: number }>
  >({});
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState<string>("");
  const [editingOriginalScore, setEditingOriginalScore] = useState<number>(0);
  const [editingNewScore, setEditingNewScore] = useState<number>(0);
  const [editingReason, setEditingReason] = useState<string>("");

  const showScoreDetails = (supplierId: string, sectionName: string) => {
    const supplier = supplierEvaluations.find(
      (s) => s.supplierID === supplierId
    );
    if (supplier) {
      const section: any = supplier.sections.find(
        (s: any) => s.sectionName === sectionName
      );
      if (section && section.users) {
        setScoreDetails(section.users);
        setCurrentSection(sectionName);
        setCurrentSupplier(supplier.supplierName);
        setCurrentSectionScore(section.sectionScore);
        setCurrentMaxScore(section.maxScore);
        setCurrentQuestions(section.questions || []);
        setOpenEvaluationScoreDrawer(true);
      }
    }
  };

  const allSections = useMemo(() => {
    const sections = new Set<string>();
    supplierEvaluations.forEach((supplier: any) => {
      supplier.sections.forEach((section: any) => {
        sections.add(section.sectionName);
      });
    });
    return Array.from(sections);
  }, [supplierEvaluations]);

  const getOriginalOverallScore = (supplierId: string) => {
    const supplier = supplierEvaluations.find(
      (s) => s.supplierID === supplierId
    );
    if (!supplier) return 0;
    return supplier.overallScore || (
      supplier.sections.reduce(
        (sum: number, section: any) => sum + section.sectionScore,
        0
      ) / supplier.sections.length
    );
  };

  // Highest score per section
  const highestScorePerSection = useMemo(() => {
    const result: Record<string, string> = {};
    allSections.forEach((sectionName) => {
      let maxScore = -1;
      let maxSupplierId = "";
      supplierEvaluations.forEach((supplier) => {
        const section = supplier.sections.find(
          (s: any) => s.sectionName === sectionName
        );
        if (section && section.sectionScore > maxScore) {
          maxScore = section.sectionScore;
          maxSupplierId = supplier.supplierID;
        }
      });
      result[sectionName] = maxSupplierId;
    });
    return result;
  }, [supplierEvaluations, allSections]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const sorted = [...supplierEvaluations].sort(
      (a, b) => (a.rank || 999) - (b.rank || 999)
    );
    const topRanked = sorted[0];
    const highestScoreSupplier = [...supplierEvaluations].sort((a, b) => {
      const aScore =
        adjustedScores[a.supplierID]?.score ??
        getOriginalOverallScore(a.supplierID);
      const bScore =
        adjustedScores[b.supplierID]?.score ??
        getOriginalOverallScore(b.supplierID);
      return bScore - aScore;
    })[0];
    const highestScore = highestScoreSupplier
      ? adjustedScores[highestScoreSupplier.supplierID]?.score ??
        getOriginalOverallScore(highestScoreSupplier.supplierID)
      : 0;

    return {
      topRanked: topRanked?.supplierName || "-",
      highestScoreName: highestScoreSupplier?.supplierName || "-",
      highestScore,
      supplierCount: supplierEvaluations.length,
      sectionCount: allSections.length,
    };
  }, [supplierEvaluations, adjustedScores, allSections]);

  // --- Transposed data: one row per supplier ---
  const dataSource = useMemo(() => {
    return supplierEvaluations.map((supplier, index) => {
      const originalScore = getOriginalOverallScore(supplier.supplierID);
      const adjusted = adjustedScores[supplier.supplierID];
      const displayScore = adjusted ? adjusted.score : originalScore;
      const row: any = {
        key: supplier.supplierID,
        supplierID: supplier.supplierID,
        supplierName: supplier.supplierName,
        supplierIndex: index,
        rank: supplier.rank,
        overallScore: {
          displayScore,
          originalScore,
          adjusted: !!adjusted,
          reason: adjusted?.reason || "",
        },
      };
      // One key per section
      allSections.forEach((sectionName) => {
        const section = supplier.sections.find(
          (s: any) => s.sectionName === sectionName
        );
        row[sectionName] = section
          ? {
              score: section.sectionScore,
              maxScore: section.maxScore,
              isHighest:
                highestScorePerSection[sectionName] === supplier.supplierID,
            }
          : null;
      });
      return row;
    });
  }, [supplierEvaluations, adjustedScores, allSections, highestScorePerSection]);

  // --- Columns: Supplier | Section1 | Section2 | ... | Overall Score ---
  const columns = useMemo((): ColumnsType<any> => {
    const cols: ColumnsType<any> = [
      {
        title: "Supplier",
        dataIndex: "supplierName",
        key: "supplier",
        fixed: "left",
        width: 220,
        render: (_: any, record: any) => {
          const rank = record.rank;
          const rankStyle = rankColors[rank] || {
            bg: "#f5f5f5",
            color: "#888",
            label: `${rank}th`,
          };
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  backgroundColor:
                    avatarColors[record.supplierIndex % avatarColors.length],
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {getSupplierInitials(record.supplierName)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#333",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 130,
                  }}
                  title={record.supplierName}
                >
                  {record.supplierName}
                </div>
                {rank && (
                  <Tag
                    style={{
                      background: rankStyle.bg,
                      color: rankStyle.color,
                      border: "none",
                      fontSize: 10,
                      lineHeight: "16px",
                      padding: "0 6px",
                      fontWeight: 600,
                      marginTop: 2,
                    }}
                  >
                    <TrophyOutlined style={{ marginRight: 2 }} />
                    {rankStyle.label}
                  </Tag>
                )}
              </div>
            </div>
          );
        },
      },
    ];

    // Section columns
    allSections.forEach((sectionName) => {
      cols.push({
        title: (
          <div style={{ fontSize: 12, fontWeight: 600, lineHeight: "16px" }}>
            {sectionName}
          </div>
        ),
        dataIndex: sectionName,
        key: sectionName,
        align: "center",
        width: 160,
        render: (value: any, record: any) => {
          if (!value) return <span style={{ color: "#ccc" }}>N/A</span>;
          const { score, maxScore, isHighest } = value;
          const pct = Math.round((score / maxScore) * 100);
          const color = getScoreColor(score, maxScore);

          return (
            <div
              style={{
                padding: "4px 8px",
                borderRadius: 6,
                background: isHighest ? "#f0fdf4" : "transparent",
                cursor: "pointer",
              }}
              onClick={() =>
                showScoreDetails(record.supplierID, sectionName)
              }
            >
              <Progress
                percent={pct}
                size="small"
                strokeColor={color}
                showInfo={false}
                style={{ marginBottom: 2 }}
              />
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#555",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                }}
              >
                <span>
                  {score}/{maxScore}
                </span>
                {isHighest && (
                  <StarOutlined style={{ fontSize: 10, color: "#52c41a" }} />
                )}
              </div>
            </div>
          );
        },
      });
    });

    // Overall Score column
    cols.push({
      title: (
        <div style={{ fontSize: 12, fontWeight: 600, color: "#1890ff" }}>
          Overall Score
        </div>
      ),
      dataIndex: "overallScore",
      key: "overallScore",
      align: "center",
      width: 180,
      fixed: "right",
      render: (value: any, record: any) => {
        const { displayScore, originalScore, adjusted, reason } = value;
        const isIncreased = adjusted && displayScore > originalScore;
        const isDecreased = adjusted && displayScore < originalScore;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontWeight: 600,
              color: "#1890ff",
            }}
          >
            <span>{displayScore.toFixed(1)}/100</span>
            {adjusted && (
              <Tooltip
                title={
                  <div>
                    <div>
                      <b>Original:</b> {originalScore.toFixed(1)}/100
                    </div>
                    <div>
                      <b>Adjusted:</b> {displayScore.toFixed(1)}/100
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <b>Reason:</b> {reason}
                    </div>
                  </div>
                }
              >
                {isIncreased ? (
                  <Tag
                    color="green"
                    style={{
                      fontSize: 11,
                      padding: "0 4px",
                      lineHeight: "18px",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowUpOutlined /> +
                    {(displayScore - originalScore).toFixed(1)}
                  </Tag>
                ) : isDecreased ? (
                  <Tag
                    color="red"
                    style={{
                      fontSize: 11,
                      padding: "0 4px",
                      lineHeight: "18px",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowDownOutlined />{" "}
                    {(displayScore - originalScore).toFixed(1)}
                  </Tag>
                ) : null}
              </Tooltip>
            )}
            <EditOutlined
              style={{ fontSize: 13, cursor: "pointer", color: "#1890ff" }}
              onClick={() =>
                handleEditOverallScore(record.supplierID, originalScore)
              }
            />
          </div>
        );
      },
    });

    return cols;
  }, [allSections, adjustedScores, highestScorePerSection]);

  const handleEditOverallScore = (
    supplierId: string,
    originalScore: number
  ) => {
    const existing = adjustedScores[supplierId];
    setEditingSupplierId(supplierId);
    setEditingOriginalScore(originalScore);
    setEditingNewScore(
      existing ? existing.score : parseFloat(originalScore.toFixed(1))
    );
    setEditingReason(existing ? existing.reason : "");
    setScoreModalOpen(true);
  };

  const handleSaveScore = async () => {
    if (!editingReason.trim()) return;
    try {
      const supplier = supplierEvaluations.find(
        (s: any) => s.supplierID === editingSupplierId
      );
      const bidId = supplier?.bidId || supplier?.supplierID;
      const response = await tenderApis.adjustScore(
        Number(tenderData?.tenderId || tenderID),
        {
          bidId: Number(bidId),
          originalScore: editingOriginalScore,
          adjustedScore: editingNewScore,
          reason: editingReason.trim(),
        }
      );
      if (response?.status || response?.data) {
        setAdjustedScores((prev) => ({
          ...prev,
          [editingSupplierId]: {
            score: editingNewScore,
            reason: editingReason.trim(),
            originalScore: editingOriginalScore,
          },
        }));
        setScoreModalOpen(false);
        setEditingReason("");
        Swal.fire("Success", "Score adjusted successfully.", "success");
      } else {
        Swal.fire("Error", response?.message || "Failed to adjust score", "error");
      }
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "Failed to adjust score", "error");
    }
  };

  const getSupplierName = (supplierId: string) => {
    const supplier = supplierEvaluations.find(
      (s) => s.supplierID === supplierId
    );
    return supplier?.supplierName || supplierId;
  };

  const handleCloseEvaluationScoreDrawer = () => {
    setOpenEvaluationScoreDrawer(false);
  };
  const handelSubmit = () => {};

  const scoreDiff = editingNewScore - editingOriginalScore;

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="table-container">
      {/* Summary Strip */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 180,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 8,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <CrownOutlined style={{ fontSize: 20, color: "#2563eb" }} />
          <div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Top Ranked</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1e3a5f" }}>
              {summaryStats.topRanked}
            </div>
            <Tag
              style={{
                background: "#fef3c7",
                color: "#a16207",
                border: "none",
                fontSize: 10,
                marginTop: 2,
              }}
            >
              Rank #1
            </Tag>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 180,
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 8,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <StarOutlined style={{ fontSize: 20, color: "#16a34a" }} />
          <div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Highest Score</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#14532d" }}>
              {summaryStats.highestScoreName}
            </div>
            <div style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
              {summaryStats.highestScore.toFixed(1)}/100
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 180,
            background: "#faf5ff",
            border: "1px solid #e9d5ff",
            borderRadius: 8,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <TeamOutlined style={{ fontSize: 20, color: "#7c3aed" }} />
          <div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>
              Suppliers Evaluated
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#3b0764" }}>
              {summaryStats.supplierCount} Suppliers
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 180,
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            borderRadius: 8,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <AppstoreOutlined style={{ fontSize: 20, color: "#ea580c" }} />
          <div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>
              Evaluation Sections
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#431407" }}>
              {summaryStats.sectionCount} Sections
            </div>
          </div>
        </div>
      </div>

      {/* Score Table — suppliers as rows, sections as columns */}
      <div id="comparison-table">
        <Form form={form} component={false}>
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            scroll={{ x: "max-content" }}
            size="small"
            bordered
            style={{ paddingTop: 10 }}
          />
        </Form>
      </div>

      <EvaluationScoreDrawer
        visible={openEvaluationScoreDrawer}
        data={{
          supplierName: currentSupplier,
          sectionName: currentSection,
          scoreDetails: scoreDetails,
          sectionScore: currentSectionScore,
          maxScore: currentMaxScore,
          questions: currentQuestions,
        }}
        onClose={handleCloseEvaluationScoreDrawer}
        onSubmit={handelSubmit}
      />

      {/* Edit Overall Score Modal */}
      <Modal
        title={
          <span>
            Adjust Overall Score - {getSupplierName(editingSupplierId)}
          </span>
        }
        open={scoreModalOpen}
        onCancel={() => setScoreModalOpen(false)}
        footer={null}
        width={480}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500, color: "#555" }}>
              Original Score
            </div>
            <InputNumber
              value={parseFloat(editingOriginalScore.toFixed(1))}
              disabled
              style={{ width: "100%" }}
              addonAfter="/ 100"
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500, color: "#555" }}>
              Adjusted Score
            </div>
            <InputNumber
              min={0}
              max={100}
              step={0.1}
              value={editingNewScore}
              onChange={(val) => setEditingNewScore(val || 0)}
              style={{ width: "100%" }}
              addonAfter="/ 100"
            />
            {scoreDiff !== 0 && (
              <div style={{ marginTop: 4 }}>
                {scoreDiff > 0 ? (
                  <Tag color="green">
                    <ArrowUpOutlined /> Increased by +{scoreDiff.toFixed(1)}
                  </Tag>
                ) : (
                  <Tag color="red">
                    <ArrowDownOutlined /> Decreased by {scoreDiff.toFixed(1)}
                  </Tag>
                )}
              </div>
            )}
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500, color: "#555" }}>
              Reason for{" "}
              {scoreDiff > 0
                ? "Increase"
                : scoreDiff < 0
                ? "Decrease"
                : "Adjustment"}{" "}
              <span style={{ color: "red" }}>*</span>
            </div>
            <Input.TextArea
              rows={3}
              value={editingReason}
              onChange={(e) => setEditingReason(e.target.value)}
              placeholder={`Enter reason for ${
                scoreDiff > 0
                  ? "increasing"
                  : scoreDiff < 0
                  ? "decreasing"
                  : "adjusting"
              } the score...`}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 4,
            }}
          >
            <Button onClick={() => setScoreModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              disabled={
                !editingReason.trim() ||
                editingNewScore ===
                  parseFloat(editingOriginalScore.toFixed(1))
              }
              onClick={handleSaveScore}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ScoreCardView;
